// Admin page for one crew member, in the cave (/cave/crew/:slug): officers
// edit their crew name, rank, section and flag (PATCH /api/roster/:id), their
// motto, favourite ship and flagship name (PATCH /api/crew/:slug/manage),
// change their role (PATCH /api/members/:id/role) or remove them from the
// crew (DELETE /api/roster/:id). Only the controls GET /api/crew/:slug/manage
// allows for this viewer are shown. The public profile at /crew/:slug is for
// viewing only.
import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type {
  CrewMemberAdmin,
  Role,
  RosterSection,
  UpdateCrewMemberProfileInput,
  UpdateRosterEntryInput,
} from '@clan-heart/shared';
import { rosterSectionSchema } from '@clan-heart/shared';
import {
  deleteRosterEntry,
  getCrewMemberAdmin,
  setMemberRole,
  updateCrewMemberProfile,
  updateRosterEntry,
} from '../api.js';
import { Button } from '../components/Button.js';
import { FlagPicker } from '../components/FlagPicker.js';
import { ShipPicker } from '../components/ShipPicker.js';
import {
  MOTTO_MAX,
  ROLE_LABELS,
  SECTION_LABELS,
  changedFields,
  officerPanelModel,
} from '../site/pages/profileEditing.js';
import styles from './CrewMemberPage.module.css';

const SECTION_ORDER = rosterSectionSchema.options;
const MAX_FLAGSHIP_NAME = 40;

interface RosterFields {
  name: string;
  rank: string;
  section: RosterSection;
  flag: string;
}

interface ProfileFields {
  motto: string;
  favouriteShip: string | null;
  flagshipName: string;
}

function profileFrom(admin: CrewMemberAdmin): ProfileFields {
  return { motto: admin.motto, favouriteShip: admin.favouriteShip, flagshipName: admin.flagshipName ?? '' };
}

/** The changed profile fields as the API takes them: a blank flagship name clears it. */
function toProfileInput(changed: Partial<ProfileFields>): UpdateCrewMemberProfileInput {
  const input: UpdateCrewMemberProfileInput = {};
  if (changed.motto !== undefined) input.motto = changed.motto.trim();
  if (changed.favouriteShip !== undefined) input.favouriteShip = changed.favouriteShip;
  if (changed.flagshipName !== undefined) input.flagshipName = changed.flagshipName.trim() || null;
  return input;
}

function fieldsFrom(admin: CrewMemberAdmin): RosterFields {
  return { name: admin.name, rank: admin.rank, section: admin.section, flag: admin.flag };
}

/** Maps the panel's own field names (which mirror CrewMemberAdmin) to
 * UpdateRosterEntryInput's: `rank` here is the roster entry's `subtitle`. */
function toRosterInput(changed: Partial<RosterFields>): UpdateRosterEntryInput {
  const input: UpdateRosterEntryInput = {};
  if (changed.name !== undefined) input.name = changed.name;
  if (changed.rank !== undefined) input.subtitle = changed.rank;
  if (changed.section !== undefined) input.section = changed.section;
  if (changed.flag !== undefined) input.flag = changed.flag;
  return input;
}

export function CrewMemberPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const query = useQuery({ queryKey: ['crew', slug, 'manage'], queryFn: () => getCrewMemberAdmin(slug) });

  if (query.isLoading) return <p>Loading…</p>;
  if (query.isError) return <p className={styles.error}>{(query.error as Error).message}</p>;
  if (!query.data) {
    return (
      <section className={styles.page}>
        <Link to="/cave/crew" className={styles.back}>
          ← Back to the crew
        </Link>
        <p>This crew member can't be found, or you may not manage them.</p>
      </section>
    );
  }
  // keyed by member, so switching members starts from a fresh form
  return <CrewMemberEditor key={query.data.userId} slug={slug} admin={query.data} />;
}

function CrewMemberEditor({ slug, admin }: { slug: string; admin: CrewMemberAdmin }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [fields, setFields] = useState<RosterFields>(() => fieldsFrom(admin));
  const [role, setRole] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [fieldsSaved, setFieldsSaved] = useState(false);
  const [profile, setProfile] = useState<ProfileFields>(() => profileFrom(admin));
  const [savedProfile, setSavedProfile] = useState<ProfileFields>(() => profileFrom(admin));
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSaved, setProfileSaved] = useState(false);
  const [roleError, setRoleError] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  function refreshLists() {
    for (const key of [['roster'], ['members'], ['accomplishments'], ['claims']]) {
      queryClient.invalidateQueries({ queryKey: key });
    }
  }

  const saveFields = useMutation({
    mutationFn: () => updateRosterEntry(admin.rosterEntryId, toRosterInput(changedFields(fieldsFrom(admin), fields))),
    onSuccess: (entry) => {
      setFieldError(null);
      setFieldsSaved(true);
      refreshLists();
      queryClient.invalidateQueries({ queryKey: ['crew'] });
      // a rename moves their profile URL: follow it
      if (entry.profileSlug && entry.profileSlug !== slug) navigate(`/cave/crew/${entry.profileSlug}`, { replace: true });
    },
    onError: (err) => setFieldError(err instanceof Error ? err.message : 'Something went wrong.'),
  });

  const saveProfile = useMutation({
    mutationFn: () => updateCrewMemberProfile(slug, toProfileInput(changedFields(savedProfile, profile))),
    onSuccess: (updated) => {
      setProfileError(null);
      setProfileSaved(true);
      setProfile(profileFrom(updated));
      setSavedProfile(profileFrom(updated));
      queryClient.invalidateQueries({ queryKey: ['crew'] });
    },
    onError: (err) => setProfileError(err instanceof Error ? err.message : 'Something went wrong.'),
  });

  const saveRole = useMutation({
    mutationFn: (next: Role) => setMemberRole(admin.userId, next as 'first_officer' | 'officer' | 'member'),
    onSuccess: () => {
      setRoleError(null);
      setRole('');
      refreshLists();
      queryClient.invalidateQueries({ queryKey: ['crew', slug] });
    },
    onError: (err) => setRoleError(err instanceof Error ? err.message : 'Something went wrong.'),
  });

  const remove = useMutation({
    mutationFn: () => deleteRosterEntry(admin.rosterEntryId),
    onSuccess: () => {
      // forget the removed member everywhere, so Back or the lists never show them
      queryClient.removeQueries({ queryKey: ['crew', slug] });
      refreshLists();
      queryClient.invalidateQueries({ queryKey: ['turned-away'] });
      navigate('/cave/crew');
    },
    onError: (err) => setRemoveError(err instanceof Error ? err.message : 'Something went wrong.'),
  });

  const panel = officerPanelModel(admin);
  const hasFieldChanges = Object.keys(changedFields(fieldsFrom(admin), fields)).length > 0;
  const hasProfileChanges = Object.keys(changedFields(savedProfile, profile)).length > 0;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFieldError(null);
    setFieldsSaved(false);
    saveFields.mutate();
  }

  function handleProfileSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setProfileError(null);
    setProfileSaved(false);
    saveProfile.mutate();
  }

  function editProfile(next: Partial<ProfileFields>) {
    setProfileSaved(false);
    setProfile({ ...profile, ...next });
  }

  function handleRoleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!role) return;
    setRoleError(null);
    saveRole.mutate(role as Role);
  }

  function handleRemove() {
    setRemoveError(null);
    if (!window.confirm(`This removes ${admin.name} from the crew, signs them out and blocks cave access.`)) return;
    remove.mutate();
  }

  return (
    <section className={styles.page}>
      <Link to="/cave/crew" className={styles.back}>
        ← Back to the crew
      </Link>

      <header className={styles.header}>
        <img src={admin.flag} alt="" />
        <div>
          <h2 className={styles.heading}>{admin.name}</h2>
          <p className={styles.meta}>
            {admin.rank || ROLE_LABELS[admin.role]} · {SECTION_LABELS[admin.section]} ·{' '}
            <Link to={`/crew/${slug}`}>View public profile</Link>
          </p>
        </div>
      </header>

      <form className={styles.card} onSubmit={handleSubmit}>
        <h3 className={styles.cardTitle}>Crew details</h3>
        <div className={styles.field}>
          <label htmlFor="cm-name">Crew name</label>
          <input
            id="cm-name"
            value={fields.name}
            maxLength={60}
            onChange={(e) => setFields({ ...fields, name: e.target.value })}
          />
        </div>
        <div className={styles.field}>
          <label htmlFor="cm-rank">Rank</label>
          <input id="cm-rank" value={fields.rank} onChange={(e) => setFields({ ...fields, rank: e.target.value })} />
        </div>
        <div className={styles.field}>
          <label htmlFor="cm-section">Section</label>
          <select
            id="cm-section"
            value={fields.section}
            onChange={(e) => setFields({ ...fields, section: e.target.value as RosterSection })}
          >
            {SECTION_ORDER.map((s) => (
              <option key={s} value={s}>
                {SECTION_LABELS[s]}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.field}>
          <span>Flag</span>
          <FlagPicker value={fields.flag} onChange={(flag) => setFields({ ...fields, flag })} />
        </div>
        {fieldError && <p className={styles.error}>{fieldError}</p>}
        {fieldsSaved && !hasFieldChanges && <p className={styles.saved}>Saved.</p>}
        <div className={styles.actions}>
          <Button type="submit" variant="primary" disabled={!hasFieldChanges || saveFields.isPending}>
            Save
          </Button>
        </div>
      </form>

      <form className={styles.card} onSubmit={handleProfileSubmit}>
        <h3 className={styles.cardTitle}>Profile</h3>
        <div className={styles.field}>
          <label htmlFor="cm-motto">Motto</label>
          <input
            id="cm-motto"
            value={profile.motto}
            maxLength={MOTTO_MAX}
            placeholder="A short line under their name"
            onChange={(e) => editProfile({ motto: e.target.value })}
          />
          <p className={styles.hint}>
            {profile.motto.length} / {MOTTO_MAX}
          </p>
        </div>
        <div className={styles.field}>
          <span>Most beloved ship</span>
          <ShipPicker value={profile.favouriteShip} onChange={(favouriteShip) => editProfile({ favouriteShip })} />
        </div>
        <div className={styles.field}>
          <label htmlFor="cm-flagship">Flagship name</label>
          <input
            id="cm-flagship"
            value={profile.flagshipName}
            maxLength={MAX_FLAGSHIP_NAME}
            placeholder="The name of their flagship"
            onChange={(e) => editProfile({ flagshipName: e.target.value })}
          />
          <p className={styles.hint}>
            {profile.flagshipName.length} / {MAX_FLAGSHIP_NAME}
          </p>
        </div>
        {profileError && <p className={styles.error}>{profileError}</p>}
        {profileSaved && !hasProfileChanges && <p className={styles.saved}>Saved.</p>}
        <div className={styles.actions}>
          <Button type="submit" variant="primary" disabled={!hasProfileChanges || saveProfile.isPending}>
            Save
          </Button>
        </div>
      </form>

      <form className={styles.card} onSubmit={handleRoleSubmit}>
        <h3 className={styles.cardTitle}>Role</h3>
        <p className={styles.readOnly}>{ROLE_LABELS[panel.currentRole]}</p>
        {panel.roleOptions.length > 0 ? (
          <>
            <div className={styles.field}>
              <label htmlFor="cm-role">Change role</label>
              <select id="cm-role" value={role} onChange={(e) => setRole(e.target.value)}>
                <option value="">— choose —</option>
                {panel.roleOptions.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </option>
                ))}
              </select>
            </div>
            {roleError && <p className={styles.error}>{roleError}</p>}
            <div className={styles.actions}>
              <Button type="submit" variant="primary" disabled={!role || saveRole.isPending}>
                Set role
              </Button>
            </div>
          </>
        ) : (
          <p className={styles.meta}>You can't change this member's role.</p>
        )}
      </form>

      {panel.showRemove && (
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>Remove from the crew</h3>
          <p className={styles.meta}>Signs them out and blocks cave access. You can let them back aboard later from the Crew page.</p>
          {removeError && <p className={styles.error}>{removeError}</p>}
          <div className={styles.actions}>
            <Button type="button" variant="danger" disabled={remove.isPending} onClick={handleRemove}>
              Remove from crew
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
